import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Client-side GET cache + in-flight request de-duplication.
//  - Identical GETs fired within CACHE_TTL_MS reuse the last response, so flipping
//    between pages shows data instantly while nothing refetches needlessly.
//  - Concurrent identical GETs share ONE network request.
//  - Any mutation (POST/PUT/PATCH/DELETE) from this client clears the cache so
//    lists reflect the write immediately.
//  - Transactional reads (availability, pending balances, prefills, lookups,
//    auth) are never cached: they must always hit the server.
// ---------------------------------------------------------------------------
const CACHE_TTL_MS = 30_000;
const NEVER_CACHE = [
  /\/auth\//, /\/pending(\?|$)/, /available/, /availability/, /prefill/, /\/lookup/,
  /\/eligible/, /\/reels\/[^/?]+$/, /\/reel-usage/, /\/other-consumptions/, /\/approvals/,
  /\/assistant\//, /\/backup/, /\/audit-logs/, /\/settings\//, /\/counters/, /\/users/,
];
const responseCache = new Map(); // key -> { ts, data, status, headers }
const inflight = new Map();      // key -> Promise<response>

function cacheKey(cfg) {
  const url = cfg.url || "";
  const params = cfg.params ? JSON.stringify(cfg.params, Object.keys(cfg.params).sort()) : "";
  return `${url}|${params}`;
}
function cacheable(cfg) {
  if ((cfg.method || "get").toLowerCase() !== "get") return false;
  if (cfg.noCache) return false;
  return !NEVER_CACHE.some((rx) => rx.test(cfg.url || ""));
}
export function clearApiCache() {
  responseCache.clear();
}

const baseAdapter = axios.getAdapter(axios.defaults.adapter);

async function cachingAdapter(cfg) {
  if (!cacheable(cfg)) {
    const res = await baseAdapter(cfg);
    if ((cfg.method || "get").toLowerCase() !== "get") clearApiCache();
    return res;
  }
  const key = cacheKey(cfg);
  const hit = responseCache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) {
    return { ...hit.res, data: structuredClone(hit.res.data), config: cfg, request: null };
  }
  if (inflight.has(key)) {
    const res = await inflight.get(key);
    return { ...res, config: cfg };
  }
  const p = baseAdapter(cfg).then((res) => {
    responseCache.set(key, { ts: Date.now(), res: { data: res.data, status: res.status, statusText: res.statusText, headers: res.headers } });
    return res;
  }).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  adapter: cachingAdapter,
});

// Attach bearer token fallback (in case cookies blocked)
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("auth_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Session expiry: on any 401 (expired/invalid token) clear the session and
// send the user to the login page instead of leaving dead pages with toasts.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const isLoginCall = (err?.config?.url || "").includes("/auth/login");
    if (status === 401 && !isLoginCall && window.location.pathname !== "/login") {
      clearApiCache();
      localStorage.removeItem("auth_token");
      window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return JSON.stringify(detail);
}

export default api;
